<script setup>
import { isEqual, isNull } from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  CHECKIN_FIELDS_IDS,
  isCheckinHistoricValueExist,
  userCanReadCheckin
} from '@/utils/check-ins/check-ins'
import { CHECKIN_ENTITY_KEYS } from '@/utils/entity-keys'

import CheckinChangedParameterResolver from '@/components/objectives/element-check-in/CheckinChangedParameterResolver'
import CheckinChangedValue from '@/components/objectives/element-check-in/CheckinChangedValue'
import AppCipheredText from '@/components/ui/AppCipheredText'

defineOptions({
  name: 'CheckinChangedParameterView'
})

const props = defineProps({
  parameter: {
    type: Object,
    required: true
  },

  okrElement: {
    type: Object,
    default: () => ({})
  }
})

const hasReadAccess = computed(() => {
  return userCanReadCheckin(props.parameter)
})

const { TO, FROM, OUTDATED, ARROW, ID, PARAMETER_ID, NAME, DATA } = CHECKIN_ENTITY_KEYS

const fromValue = computed(() => {
  return isCheckinHistoricValueExist({ parameter: props.parameter, path: FROM })
    ? {
        [NAME]: FROM,
        [DATA]: {
          ...props.parameter[FROM],
          [OUTDATED]: true
        }
      }
    : null
})

const toValue = computed(() => {
  const isToValueExist = isCheckinHistoricValueExist({ parameter: props.parameter, path: TO })

  const fromValue = props.parameter[FROM] || {}

  return isToValueExist && !isEqual(fromValue, props.parameter[TO])
    ? {
        [NAME]: TO,
        [DATA]: props.parameter[TO]
      }
    : null
})

const isNoValues = computed(() => {
  return isNull(fromValue.value) && isNull(toValue.value)
})

const displayElements = computed(() => {
  const arrow =
    fromValue.value && toValue.value
      ? {
          [NAME]: ARROW
        }
      : null

  return [fromValue.value, arrow, toValue.value].filter(Boolean)
})

const { t } = useI18n()
</script>

<template>
  <div class="ccp-View">
    <template v-if="hasReadAccess">
      <CheckinChangedValue
        v-if="parameter[ID] === CHECKIN_FIELDS_IDS.VALUE && isNoValues"
        :data="{
          [OUTDATED]: true,
          value: null
        }"
      >
        {{ t('checkin_field.view.no_value') }}
      </CheckinChangedValue>

      <CheckinChangedParameterResolver
        v-for="item in displayElements"
        :key="item[NAME]"
        :okr-element
        :parameter="{
          ...item,
          [PARAMETER_ID]: parameter[ID]
        }"
      />
    </template>

    <AppCipheredText v-else />
  </div>
</template>

<style lang="scss" scoped>
.ccp-View {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
